import { $ } from "../lib/format";
import { ACCENT, ACCENT2, AMBER, BG, BG2, BORDER, CARD, CHART_LABEL, DARK, DIM, MID, RED } from "../theme";
import { EXPENSE_CATEGORIES } from "../lib/constants";
import { IncompleteChip } from "../components/IncompleteChip";
import { formatShare, revenueCostRows, vendorBreakdown } from "../vendorBreakdown";
import { jobCompleteness, marginView } from "../jobCompleteness";
import { useBusinessType } from "../useBusinessType";
import { useDevice, useIsMobile } from "../hooks/useDevice";
import { toWholeCents } from "../money";
import { LABOR_NOTE, laborLineAmount } from "../savedEntries";
import { useState } from "react";

// ─── TAB: SYNC REVIEW ─────────────────────────────────────────────────────────

// Job types — broad list covering contractors, service businesses, and creative fields
const JOB_TYPES = [
  "General", "Remodel", "New Build", "Addition", "Roofing", "Flooring",
  "Exterior", "Structural", "Commercial", "Painting", "HVAC", "Electrical",
  "Plumbing", "Landscaping", "Cleaning", "IT Services", "Consulting",
  "Photography", "Videography", "Media Production", "Design", "Marketing",
  "Event Production", "Audio / Podcast", "Web Development", "Branding",
  "Other",
];

// ─── TAB: JOB DETAIL ─────────────────────────────────────────────────────────

// ─── (LaborSection, ManualExpenseSection, ManualRevenueSection removed) ──────
// Replaced by unified ManualEntriesSection below.

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "check", label: "Check" },
  { value: "zelle", label: "Zelle / Venmo" },
  { value: "credit_card", label: "Credit Card" },
  { value: "other", label: "Other" },
];

// ─── UNIFIED MANUAL ENTRIES SECTION (Job Detail) ────────────────────────────
// Single component combining revenue, labor, and expense entries into one table
// with a unified "+ Add Manual Entry" form.

const ENTRY_TYPES = [
  { value: "revenue", label: "Revenue" },
  { value: "labor", label: "Labor" },
  { value: "expense", label: "Job Expense" },
];

function ManualEntriesSection({ job, onAddLabor, onDeleteLabor, onAddExpense, onDeleteExpense, onAddRevenue, onDeleteRevenue, onUpdateLabor, onUpdateExpense, onUpdateRevenue }) {
  const { businessType } = useBusinessType();
  const isTrades = businessType.key === "trades"; // Trades say "Labor"; the other business types say "Contractor"
  const [adding, setAdding] = useState(false);
  const [entryType, setEntryType] = useState("expense");
  const [desc, setDesc]         = useState("");
  const [vendor, setVendor]     = useState("");
  const [amount, setAmount]     = useState("");
  const [category, setCategory] = useState("materials");
  const [method, setMethod]     = useState("check");
  const [hours, setHours]       = useState("");
  const [rate, setRate]         = useState("");
  const [flat, setFlat]         = useState(false);   // labor as one flat amount instead of hours x rate
  const [editingId, setEditingId] = useState(null);  // the saved entry being changed, if any
  const [removingId, setRemovingId] = useState(null); // an entry being removed, so a double click can't send it twice
  const [saving, setSaving]     = useState(false);   // one save at a time, so a double click can't add it twice
  const [entryDate, setEntryDate] = useState(() => new Date().toISOString().split('T')[0]);

  const laborEntries = job.laborEntries || [];
  const manualExpenses = job.manualExpenses || [];
  const manualRevenue = job.manualRevenue || [];

  // Combined entries for the table
  const allEntries = [
    ...manualRevenue.map(r => ({ ...r, _type: "revenue", _date: r.revenueDate, _detail: (r.paymentMethod || "—").replace("_", " "), _color: ACCENT2 })),
    ...laborEntries.map(l => ({ ...l, _type: "labor", _date: l.workDate, _detail: l.workerName || "—", _color: MID })),
    ...manualExpenses.map(e => ({ ...e, _type: "expense", _date: e.expenseDate, _detail: e.vendor || "—", _color: DARK })),
  ].sort((a, b) => (b._date || "").localeCompare(a._date || ""));

  const totalRevenue  = manualRevenue.reduce((s, r) => s + (r.amount || 0), 0);
  const totalLabor    = laborEntries.reduce((s, l) => s + (l.amount || 0), 0);
  const totalExpenses = manualExpenses.reduce((s, e) => s + (e.amount || 0), 0);

  // Hours x rate, rounded once to whole cents (the same rule the saved amount uses); or the flat amount typed in.
  const computedAmount = entryType === "labor"
    ? (flat ? (parseFloat(amount) > 0 ? toWholeCents(parseFloat(amount)) / 100 : null) : laborLineAmount(hours, rate))
    : null;
  const laborReady = !!computedAmount;

  function resetForm() {
    setDesc(""); setVendor(""); setAmount(""); setCategory("materials"); setMethod("check"); setHours(""); setRate(""); setFlat(false); setEditingId(null);
  }

  function startEdit(e) {
    setEntryType(e._type); setEditingId(e.id); setAdding(true);
    setDesc(e.description || ""); setEntryDate(e._date || "");
    if (e._type === "labor") {
      setVendor(e.workerName || "");
      const flatEntry = e.hours == null || e.hourlyRate == null;
      setFlat(flatEntry); setAmount(flatEntry ? String(e.amount) : ""); setHours(flatEntry ? "" : String(e.hours)); setRate(flatEntry ? "" : String(e.hourlyRate));
    } else {
      setVendor(e.vendor || ""); setAmount(String(e.amount));
      if (e._type === "expense") setCategory(e.category || "other"); else setMethod(e.paymentMethod || "other");
    }
  }

  // The form closes only when the save worked; App tells the owner plainly when it did not.
  async function handleAdd() {
    if (saving || !desc.trim()) return;
    const amt = entryType === "labor" ? (computedAmount || 0) : parseFloat(amount);
    if (!amt || amt <= 0) return;
    setSaving(true);
    let ok = false;
    try {
      if (entryType === "revenue") {
        const entry = { jobId: job.id, description: desc.trim(), amount: amt, paymentMethod: method, revenueDate: entryDate || null, source: "manual" };
        ok = editingId ? await onUpdateRevenue(editingId, entry) : await onAddRevenue(entry);
      } else if (entryType === "labor") {
        const entry = { jobId: job.id, description: desc.trim(), workerName: vendor.trim() || null, hours: !flat && hours ? parseFloat(hours) : null, hourlyRate: !flat && rate ? parseFloat(rate) : null, amount: amt, workDate: entryDate || null, source: "manual" };
        ok = editingId ? await onUpdateLabor(editingId, entry) : await onAddLabor(entry);
      } else {
        const entry = { jobId: job.id, description: desc.trim(), vendor: vendor.trim() || null, amount: amt, category, expenseDate: entryDate || null, source: "manual" };
        ok = editingId ? await onUpdateExpense(editingId, entry) : await onAddExpense(entry);
      }
    } finally { setSaving(false); }
    if (ok) { resetForm(); setAdding(false); }
  }

  async function handleDelete(entry) {
    if (removingId) return;
    setRemovingId(entry.id);
    try {
      if (entry._type === "revenue") await onDeleteRevenue(entry.id);
      else if (entry._type === "labor") await onDeleteLabor(entry.id);
      else await onDeleteExpense(entry.id);
    } finally { setRemovingId(null); }
  }

  const inputStyle = { padding:"7px 10px", borderRadius:5, border:`1px solid ${BORDER}`, background:CARD, fontFamily:"'DM Sans',sans-serif", fontSize:12, color:DARK, outline:"none", boxSizing:"border-box" };

  const typeBadge = (type) => {
    const colors = { revenue: { bg: `${ACCENT2}14`, color: ACCENT2 }, labor: { bg: `${AMBER}14`, color: AMBER }, expense: { bg: `${RED}14`, color: RED } };
    const labels = { revenue: "Revenue", labor: isTrades ? "Labor" : "Contractor", expense: "Expense" };
    const c = colors[type] || colors.expense;
    return <span style={{ fontSize:9, fontWeight:600, fontFamily:"'DM Sans',sans-serif", padding:"2px 7px", borderRadius:3, background:c.bg, color:c.color, textTransform:"uppercase", letterSpacing:"0.04em" }}>{labels[type]}</span>;
  };

  return (
    <div className="card r-card-pad" style={{ padding:"22px 26px", marginBottom:24 }}>
      <div className="r-wrap" style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, gap:10 }}>
        <div>
          <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, letterSpacing:"0.1em", color:DIM, textTransform:"uppercase", fontWeight:500, marginBottom:6 }}>Manual Entries</div>
          <div className="r-wrap" style={{ display:"flex", gap:14, fontFamily:"'DM Mono',monospace", fontSize:11 }}>
            {totalRevenue > 0 && <span style={{ color:ACCENT2 }}>+{$(totalRevenue)} revenue</span>}
            {totalLabor > 0 && <span style={{ color:AMBER }}>{$(totalLabor)} {isTrades ? "labor" : "contractors"}</span>}
            {totalExpenses > 0 && <span style={{ color:MID }}>{$(totalExpenses)} expenses</span>}
            {totalRevenue === 0 && totalLabor === 0 && totalExpenses === 0 && <span style={{ color:DIM }}>No entries yet</span>}
          </div>
        </div>
        {!adding && (
          <button className="btn act" onClick={() => setAdding(true)} style={{ fontSize:11, padding:"6px 14px" }}>
            + Add Manual Entry
          </button>
        )}
      </div>

      {/* ── Inline add form ── */}
      {adding && (
        <div style={{ padding:"14px 16px", marginBottom:14, borderRadius:6, border:`1px solid ${BORDER}`, background:BG }}>
          {/* Type selector */}
          <div style={{ display:"flex", gap:0, border:`1px solid ${BORDER}`, borderRadius:5, overflow:"hidden", marginBottom:12 }}>
            {ENTRY_TYPES.map((t, i) => (
              <button key={t.value} disabled={!!editingId && t.value !== entryType} onClick={() => { setEntryType(t.value); resetForm(); }}
                style={{ flex:1, cursor:"pointer", padding:"8px 0", fontSize:11, fontWeight:600, fontFamily:"'DM Sans',sans-serif", border:"none", borderRight:i<2?`1px solid ${BORDER}`:"none", background:entryType===t.value?ACCENT:CARD, color:entryType===t.value?CARD:MID, transition:"all 0.15s" }}>{t.value === "labor" && !isTrades ? "Contractor" : t.label}</button>
            ))}
          </div>

          <div className="r-cols-form" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
            <div>
              <label style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:DIM, fontWeight:500, display:"block", marginBottom:4 }}>Description *</label>
              <input value={desc} onChange={e => setDesc(e.target.value)} placeholder={entryType === "revenue" ? "e.g. Final payment" : entryType === "labor" ? (isTrades ? "e.g. Framing crew" : "e.g. Design help, 10 hours") : "e.g. Cash supplies"} style={{ ...inputStyle, width:"100%" }} />
            </div>
            <div>
              <label style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:DIM, fontWeight:500, display:"block", marginBottom:4 }}>
                {entryType === "labor" ? (isTrades ? "Worker / Crew" : "Contractor / Freelancer") : "Vendor / Payee"}
              </label>
              <input value={vendor} onChange={e => setVendor(e.target.value)} placeholder={entryType === "labor" ? "e.g. Marcus T." : "e.g. Acme Supply"} style={{ ...inputStyle, width:"100%" }} />
            </div>
          </div>

          {entryType === "labor" && (
            <div style={{ marginBottom:10 }}>
              <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:MID, lineHeight:1.5, marginBottom:6 }}>{LABOR_NOTE}</div>
              <label style={{ fontFamily:"'DM Sans',sans-serif", fontSize:11, color:DARK, cursor:"pointer" }}>
                <input type="checkbox" checked={flat} onChange={e => { setFlat(e.target.checked); setAmount(""); setHours(""); setRate(""); }} style={{ marginRight:6 }} />
                Enter one flat amount instead of hours and a rate
              </label>
            </div>
          )}

          <div className="r-cols-4form" style={{ display:"grid", gridTemplateColumns: entryType === "labor" && !flat ? "1fr 1fr 1fr 1fr" : "1fr 1fr 1fr", gap:10, marginBottom:12 }}>
            {entryType === "labor" && !flat ? (
              <>
                <div>
                  <label style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:DIM, fontWeight:500, display:"block", marginBottom:4 }}>Hours *</label>
                  <input type="number" min="0" step="0.5" value={hours} onChange={e => setHours(e.target.value)} placeholder="40" style={{ ...inputStyle, width:"100%" }} />
                </div>
                <div>
                  <label style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:DIM, fontWeight:500, display:"block", marginBottom:4 }}>Hourly Rate *</label>
                  <input type="number" min="0" step="0.01" value={rate} onChange={e => setRate(e.target.value)} placeholder="55.00" style={{ ...inputStyle, width:"100%" }} />
                </div>
                <div>
                  <label style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:DIM, fontWeight:500, display:"block", marginBottom:4 }}>Total</label>
                  <div style={{ ...inputStyle, background:BG2, color:DARK, fontFamily:"'DM Mono',monospace", fontWeight:600, lineHeight:"1.4" }}>
                    {computedAmount > 0 ? $(computedAmount) : "—"}
                  </div>
                </div>
              </>
            ) : (
              <div>
                <label style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:DIM, fontWeight:500, display:"block", marginBottom:4 }}>Amount *</label>
                <input type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="420.00" style={{ ...inputStyle, width:"100%" }} />
              </div>
            )}

            {entryType === "expense" && (
              <div>
                <label style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:DIM, fontWeight:500, display:"block", marginBottom:4 }}>Category</label>
                <select value={category} onChange={e => setCategory(e.target.value)} style={{ ...inputStyle, width:"100%" }}>
                  {EXPENSE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            )}

            {entryType === "revenue" && (
              <div>
                <label style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:DIM, fontWeight:500, display:"block", marginBottom:4 }}>Payment Method</label>
                <select value={method} onChange={e => setMethod(e.target.value)} style={{ ...inputStyle, width:"100%" }}>
                  {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
            )}

            <div>
              <label style={{ fontFamily:"'DM Sans',sans-serif", fontSize:10, color:DIM, fontWeight:500, display:"block", marginBottom:4 }}>Date</label>
              <input type="date" value={entryDate} onChange={e => setEntryDate(e.target.value)} style={{ ...inputStyle, width:"100%" }} />
            </div>
          </div>

          <div className="r-btnrow" style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
            <button className="btn" onClick={() => { setAdding(false); resetForm(); }} style={{ fontSize:11 }}>Cancel</button>
            <button className="btn act" onClick={handleAdd}
              disabled={saving || !desc.trim() || (entryType === "labor" ? !laborReady : (!amount || parseFloat(amount) <= 0))}
              style={{ fontSize:11, padding:"6px 16px", opacity: (saving || !desc.trim() || (entryType === "labor" ? !laborReady : (!amount || parseFloat(amount) <= 0))) ? 0.4 : 1 }}>
              {saving ? "Saving..." : editingId ? "Save changes" : "Save Entry"}
            </button>
          </div>
        </div>
      )}

      {/* ── Combined entries table ── */}
      {allEntries.length === 0 && !adding ? (
        <div style={{ padding:"20px 0", textAlign:"center" }}>
          <div style={{ fontFamily:"'Lora',serif", fontSize:14, color:MID, fontStyle:"italic", marginBottom:6 }}>No manual entries for this job</div>
          <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:12, color:DIM, marginBottom:14, lineHeight:1.6 }}>
            Add revenue, {isTrades ? "labor costs" : "contractor costs"}, or expenses that aren't in QuickBooks. Anything you add here is saved to your account.
          </div>
          <button className="btn act" onClick={() => setAdding(true)} style={{ fontSize:11, padding:"7px 18px" }}>
            + Add First Entry
          </button>
        </div>
      ) : allEntries.length > 0 && (
        <div className="r-scroll">
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12, fontFamily:"'DM Sans',sans-serif", minWidth:560 }}>
          <thead>
            <tr style={{ borderBottom:`2px solid ${BORDER}` }}>
              {["Type", "Date", "Description", "Detail", "Amount", ""].map(h => (
                <th key={h} style={{ padding:"6px 10px", textAlign: h === "Amount" ? "right" : "left", fontSize:9, letterSpacing:"0.08em", textTransform:"uppercase", color:DIM, fontWeight:600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allEntries.map((e, i) => (
              <tr key={e.id || i} style={{ borderBottom:`1px solid ${BORDER}` }}>
                <td style={{ padding:"8px 10px" }}>{typeBadge(e._type)}</td>
                <td style={{ padding:"8px 10px", fontFamily:"'DM Mono',monospace", fontSize:11, color:DIM }}>{e._date || "—"}</td>
                <td style={{ padding:"8px 10px", color:DARK, fontWeight:500, maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={e.description}>{e.description}</td>
                <td style={{ padding:"8px 10px", color:MID, fontSize:11 }}>{e._detail}</td>
                <td style={{ padding:"8px 10px", textAlign:"right", fontFamily:"'DM Mono',monospace", fontSize:11, fontWeight:600, color:e._color }}>
                  {e._type === "revenue" ? "+" : ""}{$(e.amount)}
                </td>
                <td style={{ padding:"8px 10px", textAlign:"right", whiteSpace:"nowrap" }}>
                  <button aria-label="Edit entry" onClick={() => startEdit(e)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:11, color:MID, padding:"0 8px 0 0", fontFamily:"'DM Sans',sans-serif" }} title="Edit entry">Edit</button>
                  <button className="r-close" aria-label="Remove entry" disabled={!!removingId} onClick={() => handleDelete(e)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:13, color:DIM, padding:0, lineHeight:1 }} title="Remove entry"
                    onMouseOver={ev => ev.currentTarget.style.color = RED}
                    onMouseOut={ev => ev.currentTarget.style.color = DIM}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

// Job detail: ranked list of where this job's costs went. Top 6 vendors, the rest rolled up until expanded.
function VendorBreakdownCard({ costByVendor, onJumpToInbox }) {
  const phone = useDevice() === "phone";
  const [showAll, setShowAll] = useState(false);
  const b = vendorBreakdown(costByVendor);
  const rows = showAll || !b.other ? b.rows : b.top;
  const text = phone ? 14 : 13;
  const Row = ({ name, cents, tenths, muted }) => (
    <div style={{ marginBottom:phone?14:12 }}>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"baseline",gap:10,fontFamily:"'DM Sans',sans-serif",fontSize:text }}>
        <span style={{ color:muted?MID:DARK,minWidth:0,overflowWrap:"anywhere" }}>{name}</span>
        <span style={{ color:MID,flexShrink:0,textAlign:"right" }}>{$(cents/100)} <span style={{ color:DIM }}>· {formatShare(tenths)}</span></span>
      </div>
      <div style={{ height:6,borderRadius:3,background:BG2,marginTop:5,overflow:"hidden" }}>
        <div style={{ width:`${tenths/10}%`,minWidth:tenths>0?2:0,height:"100%",background:muted?DIM:ACCENT2,opacity:0.85 }}/>
      </div>
    </div>
  );
  return (
    <div className="card r-card-pad" style={{ padding:"22px 26px" }}>
      <div style={{ ...CHART_LABEL,textTransform:"none",letterSpacing:0,fontSize:phone?14:12,color:MID }}>Cost breakdown by vendor</div>
      <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:phone?14:12,color:DIM,marginBottom:18 }}>Where did the money go?</div>
      {b.rows.length === 0 ? (
        <div style={{ padding:"32px 0",textAlign:"center" }}>
          <div style={{ fontSize:26,marginBottom:12,opacity:0.25 }}>✉</div>
          <div style={{ fontFamily:"'Lora',serif",fontSize:14,color:MID,fontStyle:"italic",marginBottom:6 }}>No expenses assigned to this job yet</div>
          <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:phone?14:12,color:DIM,marginBottom:16,lineHeight:1.6 }}>
            Assign expenses to this job in <strong style={{ color:MID }}>Expense Management</strong> to see a breakdown here.
          </div>
          {onJumpToInbox && (
            <button className="btn act" onClick={onJumpToInbox} style={{ fontSize:phone?14:11,padding:"7px 18px" }}>
              Go to Expense Management →
            </button>
          )}
        </div>
      ) : (
        <>
          {rows.map(r => <Row key={r.name} {...r} />)}
          {!showAll && b.other && <Row name={`All other vendors (${b.other.count})`} cents={b.other.cents} tenths={b.other.tenths} muted />}
          {b.other && (
            <button className="btn" onClick={() => setShowAll(v => !v)} style={{ fontSize:phone?14:12,padding:phone?"10px 14px":"6px 14px",minHeight:phone?44:undefined }}>
              {showAll ? "Show top 6 only" : "Show all vendors"}
            </button>
          )}
        </>
      )}
    </div>
  );
}

// Job detail: revenue, materials, labor and profit side by side, each labeled with its amount.
function RevenueCostsCard({ job }) {
  const phone = useDevice() === "phone";
  const { businessType } = useBusinessType();
  const rows = revenueCostRows(job, businessType);
  const text = phone ? 14 : 13;
  const color = { revenue:DIM, materials:RED, labor:AMBER, costs:RED, profit:ACCENT2 };
  return (
    <div className="card r-card-pad" style={{ padding:"22px 26px" }}>
      <div style={{ ...CHART_LABEL,textTransform:"none",letterSpacing:0,fontSize:phone?14:12,color:MID }}>Revenue and costs</div>
      <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:phone?14:12,color:DIM,marginBottom:18 }}>{businessType.key === "solo" ? "Costs & profit" : businessType.key === "team" ? "Expenses, contractors & profit" : "Material, labor & profit"}</div>
      {rows.map(r => {
        const c = r.key === "profit" && r.loss ? RED : color[r.key];
        return (
          <div key={r.key} style={{ marginBottom:phone?14:12 }}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"baseline",gap:10,fontFamily:"'DM Sans',sans-serif",fontSize:text }}>
              <span style={{ color:DARK }}>{r.label}</span>
              {r.none
                ? <span style={{ color:DIM }}>{businessType.laborWords.noneRecorded}</span>
                : <span style={{ color:r.loss ? RED : MID,flexShrink:0 }}>{$(r.cents/100)}</span>}
            </div>
            {!r.none && (
              <div style={{ height:10,borderRadius:3,background:BG2,marginTop:5,overflow:"hidden" }}>
                <div style={{ width:`${r.width}%`,minWidth:r.cents!==0?2:0,height:"100%",background:c,opacity:0.8 }}/>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function JobDetail({ job, onBack, pendingItems, onJumpToInbox, onAddLabor, onDeleteLabor, onAddExpense, onDeleteExpense, onAddRevenue, onDeleteRevenue, onUpdateLabor, onUpdateExpense, onUpdateRevenue, onUpdateJobType, jobSummaries, onJobClick, periodNote }) {
  const isMobile = useIsMobile();
  const { businessType, rule } = useBusinessType();
  const [listSearch, setListSearch] = useState("");

  if (!job) {
    const filtered = (jobSummaries || []).filter(j =>
      !listSearch || j.name.toLowerCase().includes(listSearch.toLowerCase()) || (j.clientName||"").toLowerCase().includes(listSearch.toLowerCase())
    );
    return (
      <div className="r-page" style={{ padding: isMobile ? "16px 16px 80px" : "48px 36px",background:BG,minHeight:"100vh" }}>
        <div style={{ display:"flex",alignItems:"baseline",justifyContent:"space-between",marginBottom:4 }}>
          <h1 style={{ fontFamily:"'Lora',serif",fontSize:24,fontWeight:600,color:DARK,letterSpacing:"-0.02em" }}>Job Detail</h1>
          <span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:DIM }}>{(jobSummaries||[]).length} jobs</span>
        </div>
        <p style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:DIM,marginBottom:20 }}>Select a job to see its full profitability breakdown.</p>
        {jobSummaries && jobSummaries.length > 0 && (
          <div className="card r-card-pad" style={{ padding:"18px 22px" }}>
            <div className="r-wrap" style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,gap:10 }}>
              <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:10,letterSpacing:"0.1em",color:DIM,textTransform:"uppercase",fontWeight:500 }}>Your Jobs</div>
              <input className="r-fw" type="text" value={listSearch} onChange={e=>setListSearch(e.target.value)} placeholder="Search jobs…"
                style={{ padding:"6px 12px",borderRadius:4,border:`1px solid ${BORDER}`,background:BG,fontFamily:"'DM Sans',sans-serif",fontSize:12,color:DARK,outline:"none",width:200 }}/>
            </div>
            {filtered.map(j => {
              const win = j.profit >= 0;
              const lc = jobCompleteness(j, pendingItems, rule);
              const lm = marginView(j, lc);
              return (
                <div key={j.id} onClick={() => onJobClick && onJobClick(j)}
                  style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 8px",borderBottom:`1px solid ${BORDER}`,cursor:"pointer",transition:"background 0.1s" }}
                  onMouseOver={e => e.currentTarget.style.background = BG2}
                  onMouseOut={e => e.currentTarget.style.background = "transparent"}>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:DARK,fontWeight:500,overflowWrap:"anywhere" }}>{j.name} <IncompleteChip completeness={lc} margin={lm} style={{ marginLeft:6 }}/></div>
                    {j.clientName && j.clientName !== j.name
                      ? <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:11,color:DIM,marginTop:2 }}>{j.clientName} · {j.type}</div>
                      : <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:11,color:DIM,marginTop:2 }}>{j.type}</div>
                    }
                  </div>
                  <div style={{ textAlign:"right",flexShrink:0 }}>
                    <div style={{ fontFamily:"'DM Mono',monospace",fontSize:12,fontWeight:600,color:win?ACCENT2:RED }}>{win?"+":""}{$(j.profit)}</div>
                    <div style={{ fontFamily:"'DM Mono',monospace",fontSize:10,color:lm.kind==="incomplete"?AMBER:DIM }} title={lc.reasons.join(". ")}>{lm.text}</div>
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:DIM,padding:"16px 8px",textAlign:"center" }}>No jobs match "{listSearch}"</div>
            )}
          </div>
        )}
      </div>
    );
  }
  const win = job.profit > 0;
  // This job's own completeness: what is missing from its numbers, and how to show its margin honestly.
  const completeness = jobCompleteness(job, pendingItems, rule);
  const mv = marginView(job, completeness);
  const isIncomplete = completeness.status === "incomplete";
  const marginHint = mv.kind === "incomplete" ? (completeness.flags.noCosts ? "Costs are missing" : businessType.laborWords.missing) : null;
  const invoiceLines = job.invoices.flatMap(inv => inv.Line.map(l => ({ doc:inv.DocNumber,date:inv.TxnDate,desc:l.Description,amount:l.Amount,type:"Revenue" })));
  const costLines = job.purchases.flatMap(p =>
    p.Line.filter(l => l.AccountBasedExpenseLineDetail?.CustomerRef?.value === job.id)
          .map(l => ({ doc:p.DocNumber,date:p.TxnDate,desc:l.Description,amount:l.Amount,vendor:p.EntityRef.name,type:"Cost" }))
  );
  const allLines = [...invoiceLines,...costLines].sort((a,b) => a.date.localeCompare(b.date));

  return (
    <div className="r-page" style={{ padding: isMobile ? "16px 16px 80px" : "32px 36px",background:BG,minHeight:"100vh" }}>
      <div style={{ marginBottom: isIncomplete ? 12 : 28 }}>
        {/* Back link on its own row */}
        <div style={{ marginBottom:10 }}>
          <button className="btn" onClick={onBack}>← All Jobs</button>
        </div>
        {/* Title + profit on same row */}
        <div className="r-wrap" style={{ display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:16,marginBottom:8 }}>
          <h1 style={{ fontFamily:"'Lora',serif",fontSize:24,fontWeight:600,color:DARK,letterSpacing:"-0.02em",lineHeight:1.2,minWidth:0 }}>{job.name}</h1>
          <div style={{ textAlign:"right",flexShrink:0 }}>
            <div style={{ fontFamily:"'Lora',serif",fontSize:22,fontWeight:600,color:win?ACCENT2:RED }}>{win?"+":""}{$(job.profit)}</div>
            <div style={{ fontFamily:"'DM Mono',monospace",fontSize:11,color:mv.kind==="incomplete"?AMBER:DIM,marginTop:2 }}>{mv.kind==="number" ? `${mv.text} margin` : mv.kind==="incomplete" ? "Margin incomplete" : "No margin, no revenue"}</div>
          </div>
        </div>
        {/* Meta row: client · type · status */}
        <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:DIM,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap" }}>
          {job.clientName && job.clientName !== job.name && (
            <><span>{job.clientName}</span><span style={{ color:BORDER }}>·</span></>
          )}
          <select
            value={JOB_TYPES.includes(job.type) ? job.type : "Other"}
            onChange={e => onUpdateJobType && onUpdateJobType(job.id, e.target.value)}
            style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:DIM,background:"transparent",border:"none",borderBottom:`1px dashed ${BORDER}`,cursor:"pointer",padding:"1px 2px",outline:"none" }}
            title="Change job type"
          >
            {JOB_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <span style={{ color:BORDER }}>·</span>
          <span>{job.status}</span>
        </div>
        {periodNote && <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:DIM,marginTop:8 }}>{periodNote}</div>}
      </div>

      {/* This job's own completeness: what is still missing from its numbers */}
      {isIncomplete && (
        <div className="r-stack" style={{ marginBottom:20,padding:"11px 18px",borderRadius:5,border:`1px solid rgba(140,107,48,0.3)`,background:"rgba(140,107,48,0.05)",fontSize:12,color:AMBER,fontFamily:"'DM Sans',sans-serif",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12 }}>
          <div>
            <span style={{ fontWeight:500 }}>{mv.kind === "incomplete" ? "This job's numbers are incomplete" : "This job needs a look"}</span>
            <span style={{ color:MID,marginLeft:8 }}>— {completeness.reasons.join(". ")}.{completeness.pendingCount > 0 ? " Profit may be lower than shown until you assign them." : ""}</span>
          </div>
          {completeness.pendingCount > 0 && onJumpToInbox && <button className="btn" onClick={onJumpToInbox} style={{ fontSize:11,padding:"5px 12px",flexShrink:0 }}>Review expenses →</button>}
        </div>
      )}

      {job.costs === 0 && job.revenue > 0 ? (
        <>
          <div className="r-cols-kpi" style={{ display:"grid",gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3,1fr)",gap:14,marginBottom:12 }}>
            {[
              { label:"Revenue",      val:$(job.revenue),       color:DARK },
              { label:"Gross Profit", val:"+"+$(job.revenue),   color:ACCENT2, sub:"So far, before any costs", subColor: AMBER },
              { label:"Margin",       val:mv.text,              color:mv.kind==="incomplete"?AMBER:DARK, sub: [marginHint, job.outstanding > 0 ? `${$(job.outstanding)} outstanding` : null].filter(Boolean).join(" · ") || null, subColor: AMBER },
            ].map((k,i) => (
              <div key={i} className="kpi">
                <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:10,letterSpacing:"0.1em",color:DIM,textTransform:"uppercase",marginBottom:10,fontWeight:500 }}>{k.label}</div>
                <div style={{ fontFamily:"'Lora',serif",fontSize:26,fontWeight:600,color:k.color }}>{k.val}</div>
                {k.sub && <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:10,color:k.subColor||DIM,marginTop:4 }}>{k.sub}</div>}
              </div>
            ))}
          </div>
          <div className="r-stack" style={{ marginBottom:24,padding:"11px 18px",borderRadius:5,border:`1px solid ${BORDER}`,background:CARD,fontSize:12,color:DIM,fontFamily:"'DM Sans',sans-serif",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12 }}>
            <span>No costs on this job yet, so only revenue shows. Assign expenses in Expense Management for an accurate margin.</span>
            {onJumpToInbox && <button className="btn" onClick={onJumpToInbox} style={{ fontSize:11,padding:"5px 12px",flexShrink:0 }}>Assign expenses →</button>}
          </div>
        </>
      ) : (
        (() => {
          const laborTile = { label: businessType.key === "team" ? "Contractors" : "Labor Cost", val:$(job.laborCost || 0), color:MID, sub: completeness.flags.noLabor ? (completeness.flags.laborCounts ? completeness.noLaborText : null) : job.costs > 0 ? `${Math.round(((job.laborCost||0)/job.costs)*100)}% of costs` : null, subColor: completeness.flags.noLabor && completeness.flags.laborCounts ? AMBER : null };
          const otherTile = { label: businessType.key === "team" ? "Expenses" : "Material Cost", val:$(job.materialCost || 0), color:MID, sub: job.costs > 0 ? `${Math.round(((job.materialCost||0)/job.costs)*100)}% of costs` : null };
          const costTiles = businessType.key === "solo" ? [] : businessType.key === "team" ? [laborTile, otherTile] : [otherTile, laborTile];
          const tiles = [
            { label:"Revenue",       val:$(job.revenue),                                          color:DARK },
            ...costTiles,
            { label: businessType.key === "solo" ? "Costs" : "Total Costs",   val:$(job.costs),  color:MID },
            { label:"Gross Profit",  val:(win?"+":"")+$(job.profit),                            color:win?ACCENT2:RED },
            { label:"Margin",        val:mv.text,                                                 color:mv.kind==="incomplete"?AMBER:DARK,  sub: [marginHint, job.outstanding > 0 ? `${$(job.outstanding)} outstanding` : null].filter(Boolean).join(" · ") || null, subColor: AMBER },
          ];
          return (
        <div className="r-cols-6 r-cols-tiles" style={{ display:"grid",gridTemplateColumns: isMobile ? "repeat(2,1fr)" : `repeat(${tiles.length},1fr)`,gap:14,marginBottom:24 }}>
          {tiles.map((k,i) => (
            <div key={i} className="kpi">
              <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:10,letterSpacing:"0.1em",color:DIM,textTransform:"uppercase",marginBottom:10,fontWeight:500 }}>{k.label}</div>
              <div style={{ fontFamily:"'Lora',serif",fontSize:26,fontWeight:600,color:k.color }}>{k.val}</div>
              {k.sub && <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:10,color:k.subColor||DIM,marginTop:4 }}>{k.sub}</div>}
            </div>
          ))}
        </div>
          );
        })()
      )}

      <div className="r-cols-pair" style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:18,marginBottom:24 }}>
        <VendorBreakdownCard costByVendor={job.costByVendor} onJumpToInbox={onJumpToInbox} />
        <RevenueCostsCard job={job} />
      </div>

      {/* ── Unified Manual Entries (Revenue / Labor / Expense) ── */}
      <ManualEntriesSection key={job.id} job={job} onAddLabor={onAddLabor} onDeleteLabor={onDeleteLabor} onAddExpense={onAddExpense} onDeleteExpense={onDeleteExpense} onAddRevenue={onAddRevenue} onDeleteRevenue={onDeleteRevenue} onUpdateLabor={onUpdateLabor} onUpdateExpense={onUpdateExpense} onUpdateRevenue={onUpdateRevenue} />

      <div className="card" style={{ overflow:"hidden" }}>
        <div style={{ padding:"16px 22px",borderBottom:`1px solid ${BORDER}`,background:BG }}>
          <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:DIM }}>All invoices & expenses for this job</div>
        </div>
        <div style={{ overflowX:"auto" }}>
          <table className="raw-table">
            <thead><tr>{["Date","Doc #","Type","Vendor / Description","Amount"].map(h => <th key={h}>{h}</th>)}</tr></thead>
            <tbody>
              {allLines.map((l,i) => (
                <tr key={i}>
                  <td className="mono">{l.date}</td>
                  <td className="mono">{l.doc}</td>
                  <td><span style={{ display:"inline-block",padding:"2px 9px",borderRadius:3,fontSize:10,fontWeight:500,fontFamily:"'DM Sans',sans-serif",background:l.type==="Revenue"?"rgba(92,122,90,0.1)":"rgba(140,64,64,0.08)",color:l.type==="Revenue"?ACCENT2:RED }}>{l.type}</span></td>
                  <td style={{ color:MID,maxWidth:280,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }} title={l.vendor?`[${l.vendor}] ${l.desc||""}`:l.desc||""}>{l.vendor?<span style={{ color:DIM,marginRight:8,fontFamily:"'DM Mono',monospace",fontSize:11 }}>[{l.vendor}]</span>:null}{l.desc}</td>
                  <td className="mono" style={{ color:l.type==="Revenue"&&l.amount>0?ACCENT2:RED }}>{l.amount>0?(l.type==="Revenue"?"+":"–"):""}{$(l.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export { JobDetail };
